import * as React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { Analytics } from '@dfq/shared/components/Analytics';
import { Footer } from '@dfq/shared/components/Footer';
import { NavBar, type NavBarItem, type NavBarUser } from '@dfq/shared/components/NavBar';
import { SessionExpiryWarning } from '@dfq/shared/components/SessionExpiryWarning';
import { useHeartbeat, useLogout } from '@dfq/shared/hooks';
import { cn } from '@dfq/shared/lib/utils';
import { useAppSelector } from '@dfq/shared/stores/hooks';

import { toNavBarUser } from './adapters';
import { NotificationBellContainer } from './NotificationBellContainer';
import { useStorefrontNavigation } from './useStorefrontNavigation';

/**
 * SF-AUTH-010 — Storefront customer-portal shell.
 *
 * Wraps every `/account/*`, `/cart`, and `/checkout/*` route that has
 * already passed the `RequireAuth` guard in `router/index.tsx`. Responsible
 * for three things at the shell layer, so every child page inherits them
 * without remounting per view:
 *
 *   1. Persistent top navigation — the shared `NavBar` in `surface="chrome"`
 *      mode renders the unified storefront silhouette with the portal's
 *      own nav items (Dashboard / Orders / Artwork / Addresses / Payment
 *      Methods) and the authenticated right cluster (Account dropdown +
 *      cart).
 *   2. Session keep-alive — `useHeartbeat()` (SH-AUTH-003) fires
 *      `POST /auth/heartbeat` every 60s while the tab is visible + the
 *      slice reports `isAuthenticated`. Token rotation is handled inside
 *      the hook; the shell doesn't touch tokens directly.
 *   3. Session-expiry countdown — `<SessionExpiryWarning />` (SF-AUTH-008)
 *      renders outside the `<Outlet />` so the dialog survives route
 *      transitions within the portal.
 *
 * Figma
 * - Storefront file `4puwgAMg81mAcbW487slM2` node `68:3` (Portal shell,
 *   reusing the NavBar silhouette from `43:3` / `10:2`).
 *
 * Unified chrome
 * - The portal previously hand-rolled its own `<header>` silhouette; that
 *   has been replaced by the shared `NavBar surface="chrome"` so the
 *   public and authenticated shells look identical across every page in
 *   the SPA. The dark `<Footer />` below the `<Outlet />` pulls the same
 *   admin-configured columns, brand block, and contact line as
 *   `PublicLayout` (SF-NAV-101, see `./useStorefrontNavigation.ts`).
 *
 * Auth wiring
 * - `auth.user` is read via the typed `useAppSelector`. Display name falls
 *   back to "Account" when the slice hasn't populated yet (first paint of
 *   a restored session before the heartbeat completes).
 * - Sign-out uses `useLogout()` (SF-AUTH-009) which revokes server-side,
 *   clears tokens, resets Redux, and navigates to `/login`.
 */

interface CustomerNavItem {
  /** Display label — also used as the NavBar item label. */
  label: string;
  /** Route to navigate to — passed through as `NavBarNavLink.to`. */
  to: string;
}

const CUSTOMER_NAV_ITEMS: readonly CustomerNavItem[] = [
  { label: 'Dashboard', to: '/account' },
  { label: 'Orders', to: '/account/orders' },
  { label: 'Artwork Library', to: '/account/artwork' },
  { label: 'Addresses', to: '/account/addresses' },
  { label: 'Payment Methods', to: '/account/payment-methods' },
];

export interface CustomerLayoutProps {
  /** Optional override for the nav items — primarily a story / test knob. */
  navItems?: readonly CustomerNavItem[];
  /** Optional className applied to the outer shell element. */
  className?: string;
}

/**
 * Defensive cart-count selector. Matches the shape used by `PublicLayout`
 * so the portal renders the same "Cart (N)" chrome label if/when a cart
 * slice ever registers. Until SF-CART-* lands this resolves to `0`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const selectCartCount = (state: any): number =>
  typeof state?.cart?.itemCount === 'number' ? state.cart.itemCount : 0;

export function CustomerLayout({
  navItems = CUSTOMER_NAV_ITEMS,
  className,
}: CustomerLayoutProps = {}) {
  const navigate = useNavigate();
  const logout = useLogout();
  const user = useAppSelector((state) => state.auth.user);
  const cartCount = useAppSelector(selectCartCount);
  const { footerColumns, footerBrand, footerContact, footerCopyrightText, headerLogoUrl } =
    useStorefrontNavigation();

  // Mount the sliding-window keep-alive once at the shell — every portal
  // page benefits without remounting the interval on every navigation.
  useHeartbeat();

  const navBarUser: NavBarUser | undefined = React.useMemo(() => toNavBarUser(user), [user]);

  const handleAccountSettings = React.useCallback(() => {
    navigate('/account/profile');
  }, [navigate]);

  const handleLogout = React.useCallback(() => {
    // `useLogout` returns a memoized async fn; swallow the promise here so
    // the DropdownMenuItem.onSelect consumer stays synchronous.
    void logout();
  }, [logout]);

  // Map the portal's flat nav list into the shared NavBarItem shape. These
  // are all plain links — no dropdown groups in the portal.
  const navBarItems: NavBarItem[] = React.useMemo(
    () => navItems.map((item) => ({ label: item.label, to: item.to })),
    [navItems],
  );

  return (
    <div
      className={cn('bg-background flex min-h-screen flex-col', className)}
      data-testid="customer-layout"
    >
      {/* SF-INFRA-003 — GA4 page_view emission for portal routes. */}
      <Analytics />

      <NavBar
        surface="chrome"
        navItems={navBarItems}
        cartCount={cartCount}
        cartHref="/cart"
        user={navBarUser}
        onAccountClick={handleAccountSettings}
        onSignOut={handleLogout}
        notificationsSlot={<NotificationBellContainer />}
        logoSrc={headerLogoUrl}
      />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-screen-xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      <Footer
        variant="dark"
        columns={footerColumns}
        brand={footerBrand}
        contact={footerContact}
        copyrightText={footerCopyrightText}
      />

      {/* SF-AUTH-008 countdown dialog — mounted OUTSIDE <Outlet /> so it
          survives navigation between portal pages. The component renders
          `null` while unauthenticated, so the shell stays quiet on the
          first paint after hydration. */}
      <SessionExpiryWarning />
    </div>
  );
}
